import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@playbookai/ui";
import type { AuthScope, SolveDetail, SolvePhase } from "@playbookai/types";
import { useApiClient } from "../api/context.js";
import { solvesApi } from "../api/playbook-be-client.js";
import {
  solveDetailQueryKey,
  useSolveMutationFence,
} from "../access/auth-scope.js";

export interface SetPhaseLockVars {
  solveId: string;
  phase: SolvePhase;
  locked: boolean;
}

/**
 * Lock/unlock a single phase (ADR-0019). The endpoint returns just the
 * affected phase's new state (`{ phase, locked }`), so onSuccess we patch the
 * cached `SolveDetail`'s `lockedPhases` set accordingly.
 */
export function useSetPhaseLock() {
  const config = useApiClient();
  const qc = useQueryClient();
  const scopeFence = useSolveMutationFence();
  return useMutation<
    { phase: SolvePhase; locked: boolean },
    Error,
    SetPhaseLockVars,
    AuthScope | null
  >({
    mutationFn: ({ solveId, phase, locked }) =>
      solvesApi.setPhaseLock(config, solveId, phase, locked),
    onMutate: scopeFence.capture,
    onSuccess: (result, { solveId }, invocationScope) => {
      if (!scopeFence.isCurrent(invocationScope)) return;
      qc.setQueryData<SolveDetail>(
        solveDetailQueryKey(invocationScope, solveId),
        (prev) => {
          if (!prev) return prev;
          const next = new Set(prev.lockedPhases ?? []);
          if (result.locked) next.add(result.phase);
          else next.delete(result.phase);
          return { ...prev, lockedPhases: [...next] };
        },
      );
    },
    onError: () => {
      toast("Couldn't change the phase lock. Please try again.", {
        title: "Lock change failed",
        tone: "critical",
      });
    },
  });
}
